import * as React from 'react';
import DisplayElementRenderer from '../PageElements/DisplayElementRenderer';
import InputElementRenderer from '../PageElements/InputElements/InputElementRenderer';
import Coach from '../PageElements/Coach/Coach';
import { PageData } from '../../../data/datatypes';
import { useLocation } from 'react-router-dom';
import { getPageState, savePageState } from '../../../services/DBService/DbService';
import { useAppDispatch, useAppSelector, useAutoSaveOnPageLeave } from '../../../store/hooks';
import { InputAudioElement, InputCheckElement, InputDurationElement, InputNumberElement, InputRangeElement, InputSelectElement, InputTextElement, PageElement, PageStateDB } from '@app/common';
import { setUserVariables } from '../../../store/globalStateSlice';
import _ from 'underscore';
import InputAudio from '../PageElements/InputElements/InputAudio/InputAudio';
import InputModifier from '../PageElements/InputElements/InputModifier/InputModifier';
import { setElementValue } from 'src/store/moduleStateSlice';
import Gps from '../PageElements/Gps/Gps';
import { AudioService } from 'src/services/CordovaService/AudioService';
import { getOrderedContentIds } from '../../../utils/contentOrder';

type Properties = {
    page: PageData;
    sectionId: `section_${string}`;
    itemId: `item_${string}`;
    setNextButtonDisabled: (disabled: boolean) => void;
};

export default function Page(props: Properties) {
    const { page, sectionId, itemId, setNextButtonDisabled } = props;
    const { elements } = page;
    const path = useLocation().pathname.replace(/\//g, "__");
    const audioService = new AudioService();
    const moduleState = useAppSelector(state => state.moduleState.currentModuleState);
    const sessionState = useAppSelector(state => state.moduleState.transientStateProperties).sort();
    const userVariables = useAppSelector(state => state.globalState.user?.variables || {});
    const userCode = useAppSelector(state => state.globalState.user?.code);
    const dispatch = useAppDispatch();

    const [pageState, setPageState] = React.useState<PageStateDB>();
    const [variables, setVariables] = React.useState<{ [name: string]: any }>({ ...userVariables });
    const [audioBlobs, setAudioBlobs] = React.useState<Record<string, Blob>>({});

    React.useEffect(() => {
        // reset audio blobs
        setAudioBlobs({});
    }, [elements]);

    function extractModuleIdFromPath(path: string): string | null {
        const parts = path.split("__").filter(Boolean);
        return parts[1] || null;
    }

    const getPageScopedAudioBlobs = React.useCallback((): Record<string, Blob> => {
        const moduleId = extractModuleIdFromPath(path);
        if (!moduleId) {
            return {};
        }
        const filename = audioService.getAudioFileName(userCode, moduleId);
        return Object.keys(elements).reduce((acc, elementId) => {
            if (audioBlobs[elementId]) {
                acc[filename] = audioBlobs[elementId];
            }
            return acc;
        }, {} as Record<string, Blob>);
    }, [elements, userCode, moduleState._id, audioBlobs, path]);

    useAutoSaveOnPageLeave(
        [pageState, variables, audioBlobs, getPageScopedAudioBlobs],
        async () => {
            if (variables && Object.keys(variables).length > 0 && !_.isEqual(variables, userVariables)) {
                dispatch(setUserVariables(variables));
            }
            if (pageState) {
                // save pageState
                const blobs = getPageScopedAudioBlobs();
                // console.log('[useAutoSaveOnPageLeave] blobs: ', blobs);
                await savePageState(pageState, blobs);
            }
        },
        [path],
        async () => {
            const state = await getPageState(sessionState?.length > 0 ? `page_${path}?${sessionState.join('&')}` : `page_${path}`);
            // we even want the state to be set when undefined,
            // since there might be an old pageState in place
            // from the previous page
            setPageState(state);
        },
        true
    );
    // update user variables if they change
    /*
    React.useEffect(() => {
        setVariables(old => ({
            ...old,
            userVariables
        }));
    }, [userVariables]);
    */

    // possibly disable next-button if page-state changes
    React.useEffect(() => {
        let nextButtonDisabled = false;
        Object.keys(elements).forEach(elementId => {
            const element = elements[elementId as `element_${string}`] as PageElement;
            const elementsWithRequiredField = ['input_text', 'input_select', 'input_check', 'input_list', 'input_range', 'input_audio', 'input_number', 'input_duration'];
            if (!elementsWithRequiredField.includes(element.type)) {
                return;
            }
            const inputElement = element as InputTextElement | InputSelectElement | InputCheckElement | InputRangeElement | InputAudioElement | InputNumberElement | InputDurationElement;
            if (inputElement.required) {
                if (inputElement.type === 'input_text' && inputElement.variable !== undefined) {
                    if (variables[inputElement.variable] === undefined || variables[inputElement.variable] === '') {
                        nextButtonDisabled = true;
                    }
                } else if (inputElement.type === 'input_range') {
                    if (pageState?.values[elementId] === undefined) {
                        nextButtonDisabled = true;
                    }
                } else if (inputElement.type === 'input_duration') {
                    try {
                        const parsed = pageState?.values[elementId] ? JSON.parse(pageState?.values[elementId]) : null;
                        const hours = parsed?.hours ?? 0;
                        const minutes = parsed?.minutes ?? 0;
                        const seconds = parsed?.seconds ?? 0;

                        const isMinutesDisabled = (inputElement as InputDurationElement).disableMinutes === true;
                        const isSecondsDisabled = isMinutesDisabled || (inputElement as InputDurationElement).disableSeconds === true;

                        const isEmpty =
                            hours === 0 &&
                            (isMinutesDisabled || minutes === 0) &&
                            (isSecondsDisabled || seconds === 0);

                        if (isEmpty) {
                            nextButtonDisabled = true;
                        }
                    } catch {
                        nextButtonDisabled = true; // invalid JSON or missing data
                    }
                } else if (!pageState ||
                    !pageState.values[elementId] ||
                    pageState.values[elementId] === '' ||
                    (Array.isArray(pageState.values[elementId]) && pageState.values[elementId].length === 0)
                ) {
                    nextButtonDisabled = true;
                }
            }
        });
        setNextButtonDisabled(nextButtonDisabled);
    }, [pageState, variables, path]);

    const setVariable = (varName: string, value: any) => {
        setVariables(old => ({
            ...old,
            [varName]: value
        }));
    };

    const setFieldValue = async (fieldId: string, value: any) => {
        setPageState(old => {
            if (!old) {
                return {
                    _id: sessionState?.length > 0 ? `page_${path}_session-state-${sessionState.join('&')}` : `page_${path}`,
                    values: {
                        [fieldId]: value
                    }
                };
            } else {
                return {
                    ...old,
                    values: {
                        ...old.values,
                        [fieldId]: value
                    }
                };
            }
        });

        const compoundKey = `${sectionId}__${itemId}__${fieldId}`;

        dispatch(setElementValue({
            key: compoundKey,
            value
        }));
    };

    const handleSetAudioBlob = React.useCallback((elementId: string, blob: Blob) => {
        setAudioBlobs((prev) => ({ ...prev, [elementId]: blob }));
    }, []);

    let content = (
        getOrderedContentIds(Object.keys(elements)).map((itemId) => {
            const item = elements[itemId as `element_${string}`] as PageElement;
            switch (item.type) {
                case 'coach':
                    return <Coach
                        key={itemId}
                        data={item}
                    />;
                case 'text':
                case 'caption':
                case 'listing':
                case 'image':
                case 'video':
                case 'audio':
                case 'youtube':
                case 'info_term':
                    return <DisplayElementRenderer
                        key={itemId}
                        element={item}
                    />;

                case 'input_text':
                case 'input_select':
                case 'input_range':
                case 'input_check':
                case 'input_number':
                case 'input_duration':
                    if (item.type === 'input_text' && item.variable !== undefined) {
                        return (
                            <InputElementRenderer
                                key={itemId}
                                element={item}
                                value={variables[item.variable as string] || ''}
                                setValue={(value: any) => setVariable(item.variable as string, value)}
                            />
                        );
                    } else {
                        return (
                            <InputElementRenderer
                                key={itemId}
                                element={item}
                                value={pageState ? pageState.values[itemId] : undefined}
                                setValue={(value: any) => setFieldValue(itemId, value)}
                            />
                        );
                    }

                case 'input_modifiers':
                    return <InputModifier
                        key={itemId}
                        data={item}
                    />;

                case 'input_audio':
                    return (
                        <InputAudio
                            key={itemId}
                            docId={pageState?._id}
                            moduleId={moduleState._id}
                            elementId={itemId}
                            data={item}
                            value={pageState ? pageState.values[itemId] : undefined}
                            setValue={(value: any) => setFieldValue(itemId, value)}
                            setBlob={handleSetAudioBlob}
                        />
                    );

                case 'gps':
                    return (
                        <Gps
                            key={itemId}
                            value={pageState ? pageState.values[itemId] : undefined}
                            setValue={(value: any) => setFieldValue(itemId, value)}
                        />
                    );

                default:
                    let error = typeof (item as any)?.type === 'string'
                        ? (item as any)?.type
                        : 'item.type is no string';
                    return <p>unknown page item type: {error}</p>;
            }
        })
    );
    return (
        <div className="page">
            {content}
        </div>
    );
}